import { useState } from "react";
import { Modal } from "react-bootstrap";
import { useLanguage } from "../../../i18n/LanguageProvider";
import { SignalSource } from "../apiService";
import { ArrowRight } from "../../icons";

export const SWEDISH_TZ = "Europe/Stockholm";

export const fmtTime = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("sv-SE", {
    timeZone: SWEDISH_TZ,
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Map polarity to a direction class. The threshold keeps near-zero signals neutral.
export const polarityClass = (p: number): "up" | "down" | "flat" =>
  p > 0.1 ? "up" : p < -0.1 ? "down" : "flat";

// Locale-aware "x ago" for the summary's production time.
export const relativeAgo = (iso: string | null, locale: string): string => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const diffSec = Math.round((then - Date.now()) / 1000); // negative = past
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(Math.round(diffSec), "second");
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), "hour");
  return rtf.format(Math.round(diffSec / 86400), "day");
};

// Short label for a source chip: prefer the publication, fall back to the title.
const sourceLabel = (s: SignalSource): string => (s.source || s.title || "Källa").trim();

// Only show "read original" links that actually point at a public web page —
// some sources (e.g. ENTSO-E outage records) carry a local file path, not a URL.
const isWebUrl = (u: string | null): boolean => !!u && /^https?:\/\//i.test(u.trim());

// One publication (ENTSO-E, BalticWind, …) and the documents behind it.
export interface SourceGroup {
  label: string;
  docs: SignalSource[];
}

// Collapse duplicate citations into one chip per publication, preserving order
// of first appearance. Identical documents (same title + url) are de-duplicated
// within a group — the backend can return the same record several times (e.g.
// repeated ENTSO-E outage revisions), which would otherwise inflate the count
// and list the same line twice.
export const groupSources = (sources: SignalSource[] | undefined): SourceGroup[] => {
  if (!sources || sources.length === 0) return [];
  const order: string[] = [];
  const byLabel = new Map<string, SignalSource[]>();
  const seen = new Map<string, Set<string>>();
  for (const s of sources) {
    const label = sourceLabel(s);
    if (!byLabel.has(label)) {
      byLabel.set(label, []);
      seen.set(label, new Set());
      order.push(label);
    }
    // Key on the title when present — for ENTSO-E outages the title already
    // encodes the border + window, so revisions / re-emitted mRIDs (which only
    // differ in the url) collapse to one entry. Fall back to url for the rare
    // titleless source.
    const key = (s.title || "").trim() || (s.url || "").trim();
    if (seen.get(label)!.has(key)) continue;
    seen.get(label)!.add(key);
    byLabel.get(label)!.push(s);
  }
  return order.map((label) => ({ label, docs: byLabel.get(label)! }));
};

// Shared source-citation state: clicking a chip opens the document popup for that
// publication's group.
export const useSourceModal = () => {
  const [activeGroup, setActiveGroup] = useState<SourceGroup | null>(null);
  return { activeGroup, setActiveGroup };
};

// One chip per distinct publication, with a count badge (ENTSO-E · 2).
export const SourceChips = ({
  sources,
  onPick,
}: {
  sources: SignalSource[] | undefined;
  onPick: (g: SourceGroup) => void;
}) => {
  const { t } = useLanguage();
  const groups = groupSources(sources);
  if (groups.length === 0) return null;
  return (
    <div className="signal-sources">
      <span className="signal-sources__label">{t("signals.sources")}</span>
      {groups.map((g) => (
        <button
          key={g.label}
          type="button"
          className="signal-source-chip"
          onClick={() => onPick(g)}
        >
          {g.label}
          <span className="signal-source-chip__count">{g.docs.length}</span>
        </button>
      ))}
    </div>
  );
};

// Document popup for one publication: lists each document title (summaries omitted
// — they read poorly), with a link only when it points at a real web page.
export const SourceModal = ({
  group,
  onClose,
}: {
  group: SourceGroup | null;
  onClose: () => void;
}) => {
  const { t } = useLanguage();
  return (
    <Modal show={!!group} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="signal-doc__title">
          {group?.label || t("signals.sourceFallback")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ul className="signal-doc__list">
          {group?.docs.map((d, i) => (
            <li key={`${d.url || d.title || "doc"}-${i}`} className="signal-doc__item">
              <span className="signal-doc__doc-title">
                {d.title || t("signals.sourceFallback")}
              </span>
              {isWebUrl(d.url) && (
                <a
                  className="signal-doc__link"
                  href={d.url!}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("signals.readOriginal")}{" "}
                  <ArrowRight style={{ width: "0.85em", height: "0.85em", verticalAlign: "-0.1em" }} />
                </a>
              )}
            </li>
          ))}
        </ul>
      </Modal.Body>
    </Modal>
  );
};
